import React, { useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { toast } from "react-toastify";
import {
  Button,
  Grid,
  Image,
  Message,
  Header,
  Divider,
  Segment,
} from "semantic-ui-react";
import MyTextInput from "../../../../app/common/form/MyTextInput";
import {
  functionFetchMlsCoCren,
  functionFetchMlsDataFiniti,
  functionFetchMlsIres,
} from "../../../../app/firestore/functionsService";
import { Formik, Form, useFormikContext } from "formik";
import { useSelector, useDispatch } from "react-redux";
import stateNameToAbbreviation, {
  convertAddressFull,
  ensureHttpsImageUrl,
} from "../../../../app/common/util/util";
import MyCheckbox from "../../../../app/common/form/MyCheckbox";
import MySelectInput from "../../../../app/common/form/MySelectInput";
import ModalWrapper from "../../../../app/common/modals/modalWrapper";
import { closeModal } from "../../../../app/common/modals/modalSlice";
import {
  updateDocInDb,
  updateTransactionInDb,
  updateListingAgentPartyInDb,
} from "../../../../app/firestore/firestoreService";
import { processMlsDataForForm } from "./MlsImportUtils";
import { getCitiesForMlsSearch } from "../../../../app/common/util/mlsIdCodeCities";

export default function DocFillImportMlsModal() {
  const dispatch = useDispatch();
  const { currentUserProfile } = useSelector((state) => state.profile);
  const { transaction } = useSelector((state) => state.transaction);
  const { doc } = useSelector((state) => state.doc);
  const { parties } = useSelector((state) => state.transaction);
  const isMobile = useMediaQuery({ query: "(max-width:768px)" });
const { docsTransActive, docsTransArchived, docsTransShared } = useSelector(
  (state) => state.doc
);
const { tasksTransWithDateAll } = useSelector((state) => state.task);
const [importMlsStatus, setImportMlsStatus] = useState("Initial");
const [mlsData, setMlsData] = useState(null);
const [mlsId, setMlsId] = useState("");

const agentState = currentUserProfile?.state
  ? stateNameToAbbreviation(currentUserProfile.state)
  : "CO";

let mlsOptions = [];
if (currentUserProfile?.mlsAccess?.length > 0) {
  currentUserProfile.mlsAccess.map((oneMls) => {
    if (oneMls?.mlsIdCode) {
      mlsOptions.push({
        key: oneMls.mlsIdCode,
        value: oneMls.mlsIdCode,
        text: oneMls.mlsName || "MLS",
      });
    }
    return true;
  });
}

function mlsImportSuccess(formattedData) {
  setImportMlsStatus("Complete");
  setMlsData(formattedData);
  toast.success("MLS data imported successfully");
}

async function handleMlsImport(values) {
  if (mlsId) {
    try {
      setImportMlsStatus("Loading");
      let mlsValues = null;

      // Try different MLS sources
      if (values.mlsIdCode === "CO_CREN") {
        mlsValues = await functionFetchMlsCoCren(mlsId);
      } else if (values.mlsIdCode === "CO_IRES") {
        mlsValues = await functionFetchMlsIres(mlsId);
      }

      if (mlsValues?.address) {
        mlsImportSuccess(mlsValues);
      } else {
        // Fallback to DataFiniti. Narrow that search to the cities the MLS
        // covers: prefer the user's own profile cities, otherwise fall
        // back to the mlsIdCode -> cities lookup table.
        const citiesForSearch = getCitiesForMlsSearch(
          currentUserProfile,
          values.mlsIdCode
        );
        mlsValues = await functionFetchMlsDataFiniti({
          mlsId: mlsId,
          state: agentState,
          cities: citiesForSearch,
        });
        if (mlsValues) {
          mlsImportSuccess(mlsValues);
        } else {
          setImportMlsStatus("Error");
          toast.error(
            `Error when importing MLS data. Please check the MLS ID and try again.`
          );
        }
      }
    } catch (error) {
      setImportMlsStatus("Error");
      toast.error(
        `Error when importing MLS data. Please check the MLS ID and try again.`
      );
    }
  } else {
    toast.error("Please add an MLS number");
  }
}

function WatcherMlsNum() {
  const { values } = useFormikContext();
  useEffect(() => {
    if (values.mlsNumbers && values.mlsNumbers[0]) {
      setMlsId(values.mlsNumbers[0]);
    }
  }, [values.mlsNumbers]);
  return null;
}

const initialValues = {
  mlsIdCode: currentUserProfile.mlsAccess?.[0]?.mlsIdCode || "",
  mlsNumbers: [""],
  mlsDataOptions: {
    useMlsPic: true,
    addListingAgentToParties: true,
  },
  importOptions: {
    // Address fields
    address: true,
    // Property details
    county: true,
    subdivisionName: true,
    yearBuilt: true,
    parcelNumber: true,
    legalDescription: true,
    // Required fields from user
    inclusions: true,
    exclusions: true,
    metroDistrictWebsite: true,
    emHolder: true,
    titleCo: true,
    emDepositAmount: true,
    associationFee: true,
    associationFeeFreq: true,
    ownerName: true,
    // Listing Agent Fields
    listingAgentName: true,
    listingAgentFirstName: true,
    listingAgentLastName: true,
    listingAgentEmail: true,
    listingAgentPhone: true,
    listingAgentLicense: true,
    listingAgentBrokerage: true,
  },
};

async function handleImportToForm(values) {
  if (!mlsData) {
    toast.error("Please import MLS data first");
    return;
  }
  if (!mlsData.address) {
    toast.error("Error importing MLS data. Address not found, try again.");
    return;
  }

  try {
    // Use the utility function to process MLS data and map to form fields
    const formFieldValues = processMlsDataForForm(
      mlsData,
      values.importOptions,
      doc.title || doc.name
    );

    // Update the document with the mapped form field values
    const formFieldUpdates = {};
    Object.keys(formFieldValues).forEach((fieldName) => {
      formFieldUpdates[`formFieldValues.${fieldName}`] =
        formFieldValues[fieldName];
    });

    // Prepare transaction updates
    const transactionUpdates = {};

    // Handle MLS picture import to transaction
    if (values.mlsDataOptions?.useMlsPic && mlsData.pic) {
      transactionUpdates.pic = ensureHttpsImageUrl(mlsData.pic);
    }

    // Handle address import to transaction
    if (values.importOptions?.address && mlsData.address) {
      transactionUpdates.address = {
        street: mlsData.address.street || "",
        unit: mlsData.address.unit || "",
        city: mlsData.address.city || "",
        state: mlsData.address.state || "",
        zipcode: mlsData.address.zip || mlsData.address.zipcode || "",
      };
    }

    // Handle county import to transaction
    if (values.importOptions?.county && mlsData.propertyDetails?.county) {
      if (!transactionUpdates.propertyDetails) {
        transactionUpdates.propertyDetails = {};
      }
      transactionUpdates.propertyDetails.county =
        mlsData.propertyDetails.county;
    }

    // Handle other property details import to transaction
    if (mlsData.propertyDetails) {
      if (!transactionUpdates.propertyDetails) {
        transactionUpdates.propertyDetails = {};
      }

      // Import selected property details to transaction
      const propertyDetailsFields = [
        "legalDescription",
        "inclusions",
        "exclusions",
        "yearBuilt",
        "subdivisionName",
        "parcelNumber",
      ];
      propertyDetailsFields.forEach((field) => {
        if (values.importOptions?.[field] && mlsData.propertyDetails[field]) {
          transactionUpdates.propertyDetails[field] =
            mlsData.propertyDetails[field];
        }
      });
    }

    // Handle listing agent import to parties
    let listingAgentAdded = false;
    if (
      values.mlsDataOptions?.addListingAgentToParties &&
      mlsData.listingAgent &&
      transaction
    ) {
      if (
        mlsData.listingAgent.firstName &&
        mlsData.listingAgent.lastName
      ) {
        try {
          let docs = [];
          if (docsTransActive?.docs) {
            docs = docs.concat(docsTransActive?.docs);
          }
          if (docsTransArchived?.docs) {
            docs = docs.concat(docsTransArchived?.docs);
          }
          if (docsTransShared?.docs) {
            docs = docs.concat(docsTransShared?.docs);
          }
          // Use the dedicated function to properly handle listing agent replacement
          updateListingAgentPartyInDb(
            transaction,
            mlsData,
            parties,
            docs,
            tasksTransWithDateAll,
            parties
          );
          listingAgentAdded = true;
        } catch (error) {
          console.error("Error adding listing agent to parties:", error);
          toast.warning("Could not add listing agent to parties");
        }
      }
    }

    // Update document with form field values
    if (Object.keys(formFieldUpdates).length > 0) {
      await updateDocInDb(doc.id, formFieldUpdates);
    }

    // Update transaction with MLS data
    if (Object.keys(transactionUpdates).length > 0 && transaction) {
      await updateTransactionInDb(transaction.id, transactionUpdates, true);
    }

    // Build success message
    let successMessage = "";
    const updatedItems = [];

    if (Object.keys(formFieldUpdates).length > 0) {
      updatedItems.push(`${Object.keys(formFieldUpdates).length} form fields`);
    }

    if (values.mlsDataOptions?.useMlsPic && mlsData.pic) {
      updatedItems.push("property photo");
    }

    if (values.importOptions?.address && mlsData.address) {
      updatedItems.push("property address");
    }

    if (values.importOptions?.county && mlsData.propertyDetails?.county) {
      updatedItems.push("county");
    }

    if (listingAgentAdded) {
      updatedItems.push("listing agent to parties");
    }

    if (updatedItems.length > 0) {
      successMessage = `MLS data imported successfully: ${updatedItems.join(
        ", "
      )}`;
    } else {
      successMessage = "No data was selected for import";
    }

    toast.success(successMessage);
    dispatch(closeModal({ modalType: "DocFillImportMlsModal" }));

    // Refresh the page to show updated form and transaction data
    setTimeout(() => {
      window.location.reload();
    }, 500);
  } catch (error) {
    toast.error("Error importing MLS data");
    console.error(error);
  }
}

  return (
    <ModalWrapper size="large">
      <Segment clearing>
        <div style={{ padding: "20px" }}>
          <Header size="large" color="blue">
            Import MLS Data
          </Header>
          <Divider />

          <Formik initialValues={initialValues} onSubmit={handleImportToForm}>
            {({ values, setFieldValue }) => (
              <Form>
                <Grid>
                  <Grid.Row>
                    <Grid.Column
                      mobile={16}
                      computer={4}
                      className="small padding top"
                    >
                      {currentUserProfile.mlsAccess?.[0]?.mlsName && (
                        <MySelectInput
                          key="mlsIdCode"
                          name="mlsIdCode"
                          label="MLS Name"
                          options={mlsOptions}
                        />
                      )}
                    </Grid.Column>
                    <Grid.Column
                      mobile={16}
                      computer={4}
                      className="small padding top"
                    >
                      <WatcherMlsNum />
                      <MyTextInput
                        key="mlsNum"
                        name="mlsNumbers[0]"
                        label="MLS Number"
                        value={values.mlsNumbers?.[0] || ""}
                      />
                    </Grid.Column>
                    <Grid.Column
                      mobile={16}
                      computer={4}
                      className="small padding top"
                    >
                      <Button
                        primary
                        loading={importMlsStatus === "Loading"}
                        disabled={
                          importMlsStatus === "Loading" ||
                          importMlsStatus === "Complete"
                        }
                        className={isMobile ? "fluid" : null}
                        style={{ marginTop: "20px" }}
                        onClick={(e) => {
                          e.preventDefault();
                          handleMlsImport(values);
                        }}
                      >
                        {importMlsStatus === "Initial" ||
                        importMlsStatus === "Error"
                          ? "Import MLS Details"
                          : `Import ${importMlsStatus}`}
                      </Button>
                    </Grid.Column>
                  </Grid.Row>

                  {!currentUserProfile.mlsAccess &&
                    importMlsStatus === "Error" && (
                      <Grid.Row>
                        <Grid.Column mobile={16} computer={16}>
                          <Message negative>
                            <Message.Header>
                              You do not have access to any MLS, and we could
                              not find that MLS number on the internet for your
                              area.
                            </Message.Header>
                            <p>
                              Please contact your administrator to add MLS
                              access for your account, or try again with a
                              different MLS number.
                            </p>
                          </Message>
                        </Grid.Column>
                      </Grid.Row>
                    )}

                  {mlsData && !mlsData.address ? (
                    <Grid.Row>
                      <Grid.Column mobile={16} computer={16}>
                        <Message>
                          <Message.Header>No MLS data retrieved</Message.Header>
                          <p>Please import MLS data before continuing.</p>
                        </Message>
                      </Grid.Column>
                    </Grid.Row>
                  ) : null}

                  {mlsData && mlsData.address && (
                    <>
                      <Grid.Row>
                        <Grid.Column mobile={16} computer={16}>
                          <Header as="h3" color="blue">
                            MLS Data Retrieved
                          </Header>
                          <p>
                            Select which data you want to import into the form:
                          </p>
                        </Grid.Column>
                      </Grid.Row>
                      <Grid.Row>
                        {mlsData?.pic && (
                          <Grid.Column mobile={16} computer={4}>
                            <div className="small bottom margin">
                              <Image
                                bordered
                                rounded
                                size="medium"
                                wrapped
                                src={ensureHttpsImageUrl(mlsData.pic)}
                              />
                              <br />
                              <MyCheckbox
                                name="mlsDataOptions.useMlsPic"
                                label="Import Picture to Transaction"
                              />
                            </div>
                          </Grid.Column>
                        )}
                        {mlsData?.address && (
                          <Grid.Column mobile={16} computer={4}>
                            <div className="small bottom margin">
                              <>
                                <h4>Property Address</h4>
                                <MyCheckbox
                                  name="importOptions.address"
                                  label="Property Address"
                                />
                                <p
                                  className="mini bottom margin"
                                  style={{ marginLeft: "20px", color: "#666" }}
                                >
                                  {mlsData.address.street}
                                  {mlsData.address.unit &&
                                    ` Unit ${mlsData.address.unit}`}
                                  <br />
                                  {mlsData.address.city},{" "}
                                  {mlsData.address.state} {mlsData.address.zip}
                                </p>
                              </>

                              {mlsData.propertyDetails.county && (
                                <>
                                  <MyCheckbox
                                    name="importOptions.county"
                                    label="County"
                                  />
                                  <p
                                    className="mini bottom margin"
                                    style={{
                                      marginLeft: "20px",
                                      color: "#666",
                                    }}
                                  >
                                    {mlsData.propertyDetails.county}
                                  </p>
                                </>
                              )}
                              {mlsData.propertyDetails?.yearBuilt && (
                                <div>
                                  <MyCheckbox
                                    name="importOptions.yearBuilt"
                                    label="Year Built"
                                  />
                                  <p
                                    className="mini bottom margin"
                                    style={{
                                      marginLeft: "20px",
                                      color: "#666",
                                    }}
                                  >
                                    {mlsData.propertyDetails.yearBuilt}
                                  </p>
                                </div>
                              )}
                            </div>
                          </Grid.Column>
                        )}

                        <Grid.Column mobile={16} computer={5}>
                          <div>
                            {mlsData?.propertyDetails?.legalDescription && (
                              <>
                                <h4>Legal Description</h4>
                                <MyCheckbox
                                  name="importOptions.legalDescription"
                                  label="Legal Description"
                                />
                                <p
                                  className="mini bottom margin"
                                  style={{ marginLeft: "20px", color: "#666" }}
                                >
                                  {mlsData.propertyDetails.legalDescription}
                                </p>
                              </>
                            )}
                            {mlsData.propertyDetails?.subdivisionName && (
                              <div>
                                <MyCheckbox
                                  name="importOptions.subdivisionName"
                                  label="Subdivision"
                                />
                                <p
                                  className="mini bottom margin"
                                  style={{ marginLeft: "20px", color: "#666" }}
                                >
                                  {mlsData.propertyDetails.subdivisionName}
                                </p>
                              </div>
                            )}
                          </div>
                        </Grid.Column>
                      </Grid.Row>

                      <Grid.Row>
                        <Grid.Column mobile={16} computer={16}>
                          <div className="small bottom margin">
                            <h4>Property Details</h4>

                            {mlsData.propertyDetails?.inclusions && (
                              <div style={{ width: "100%" }}>
                                <MyCheckbox
                                  name="importOptions.inclusions"
                                  label="Inclusions"
                                />
                                <p
                                  className="mini bottom margin"
                                  style={{ marginLeft: "20px", color: "#666" }}
                                >
                                  {mlsData.propertyDetails.inclusions}
                                </p>
                              </div>
                            )}
                            {mlsData.propertyDetails?.exclusions && (
                              <div style={{ width: "100%" }}>
                                <MyCheckbox
                                  name="importOptions.exclusions"
                                  label="Exclusions"
                                />
                                <p
                                  className="mini bottom margin"
                                  style={{ marginLeft: "20px", color: "#666" }}
                                >
                                  {mlsData.propertyDetails.exclusions}
                                </p>
                              </div>
                            )}
                            {mlsData.propertyDetails?.metroDistrictWebsite && (
                              <div style={{ width: "100%" }}>
                                <MyCheckbox
                                  name="importOptions.metroDistrictWebsite"
                                  label="Metro District Website"
                                />
                                <p
                                  className="mini bottom margin"
                                  style={{ marginLeft: "20px", color: "#666" }}
                                >
                                  {mlsData.propertyDetails.metroDistrictWebsite}
                                </p>
                              </div>
                            )}
                            {mlsData.propertyDetails?.earnestMoneyHolder && (
                              <div style={{ width: "100%" }}>
                                <MyCheckbox
                                  name="importOptions.emHolder"
                                  label="Earnest Money Holder"
                                />
                                <p
                                  className="mini bottom margin"
                                  style={{ marginLeft: "20px", color: "#666" }}
                                >
                                  {mlsData.customFields.earnestMoneyHolder}
                                </p>
                              </div>
                            )}
                          </div>
                        </Grid.Column>
                      </Grid.Row>
                      <Grid.Row>
                        <Grid.Column mobile={16} computer={4}>
                          <div className="small bottom margin">
                            <h4>Listing Agent</h4>
                            <MyCheckbox
                              name="mlsDataOptions.addListingAgentToParties"
                              label="Add Listing Agent to Parties"
                            />
                            {mlsData.listingAgent && (
                              <>
                                <p className="mini bottom margin">
                                  Name: {mlsData.listingAgent.firstName}{" "}
                                  {mlsData.listingAgent.lastName}
                                </p>
                                {mlsData.listingAgent.email && (
                                  <p className="mini bottom margin">
                                    Email: {mlsData.listingAgent.email}
                                  </p>
                                )}
                                {mlsData.listingAgent.phone && (
                                  <p className="mini bottom margin">
                                    Phone: {mlsData.listingAgent.phone}
                                  </p>
                                )}
                                {mlsData.listingAgent.brokerLicenseNumber && (
                                  <p className="mini bottom margin">
                                    License #:{" "}
                                    {mlsData.listingAgent.brokerLicenseNumber}
                                  </p>
                                )}
                                {mlsData.listingAgent.brokerageName && (
                                  <p className="mini bottom margin">
                                    Brokerage:{" "}
                                    {mlsData.listingAgent.brokerageName}
                                  </p>
                                )}
                                {mlsData.listingAgent.address?.city && (
                                  <p className="mini bottom margin">
                                    Address:{" "}
                                    {convertAddressFull(
                                      mlsData.listingAgent.address
                                    )}
                                  </p>
                                )}
                                {!mlsData.listingAgent.email && (
                                  <p className="mini bottom margin bold">
                                    No email found. Listing agent will still
                                    be added to Parties without an email.
                                  </p>
                                )}
                              </>
                            )}
                          </div>
                        </Grid.Column>

                        {/* Additional/Custom Fields Section */}
                        <Grid.Column mobile={16} computer={4}>
                          <div className="small bottom margin">
                            <h4>Additional Fields</h4>
                            {mlsData.customFields?.titleCo && (
                              <div>
                                {/* <MyCheckbox
                                name="importOptions.titleCo"
                                label="Title Company"
                              /> */}
                                <p
                                  className="mini bottom margin"
                                  style={{ marginLeft: "20px", color: "#666" }}
                                >
                                  {mlsData.customFields.titleCo}
                                </p>
                              </div>
                            )}
                            {mlsData.customFields?.emDepositAmount && (
                              <div>
                                <MyCheckbox
                                  name="importOptions.emDepositAmount"
                                  label="EM Deposit Amount"
                                />
                                <p
                                  className="mini bottom margin"
                                  style={{ marginLeft: "20px", color: "#666" }}
                                >
                                  {mlsData.customFields.emDepositAmount}
                                </p>
                              </div>
                            )}
                            {mlsData.customFields?.associationFee && (
                              <div>
                                <MyCheckbox
                                  name="importOptions.associationFee"
                                  label="Association Fee"
                                />
                                <p
                                  className="mini bottom margin"
                                  style={{ marginLeft: "20px", color: "#666" }}
                                >
                                  {mlsData.customFields.associationFee}
                                </p>
                              </div>
                            )}
                            {mlsData.customFields?.associationFeeFreq && (
                              <div>
                                <MyCheckbox
                                  name="importOptions.associationFeeFreq"
                                  label="Association Fee Frequency"
                                />
                                <p
                                  className="mini bottom margin"
                                  style={{ marginLeft: "20px", color: "#666" }}
                                >
                                  {mlsData.customFields.associationFeeFreq}
                                </p>
                              </div>
                            )}
                            {mlsData.customFields?.ownerName && (
                              <div>
                                <MyCheckbox
                                  name="importOptions.ownerName"
                                  label="Owner Name"
                                />
                                <p
                                  className="mini bottom margin"
                                  style={{ marginLeft: "20px", color: "#666" }}
                                >
                                  {mlsData.customFields.ownerName}
                                </p>
                              </div>
                            )}
                          </div>
                        </Grid.Column>
                      </Grid.Row>

                      <Grid.Row>
                        <Grid.Column mobile={16} computer={16}>
                          <div
                            style={{ textAlign: "right", marginTop: "20px" }}
                          >
                            <Button
                              onClick={() =>
                                dispatch(
                                  closeModal({
                                    modalType: "DocFillImportMlsModal",
                                  })
                                )
                              }
                              style={{ marginRight: "10px" }}
                            >
                              Cancel
                            </Button>
                            <Button primary type="submit" disabled={!mlsData}>
                              Import Selected Data
                            </Button>
                          </div>
                        </Grid.Column>
                      </Grid.Row>
                    </>
                  )}

                  {/* Always show close button */}
                  {!mlsData && (
                    <Grid.Row>
                      <Grid.Column mobile={16} computer={16}>
                        <div style={{ textAlign: "right", marginTop: "20px" }}>
                          <Button
                            onClick={() =>
                              dispatch(
                                closeModal({
                                  modalType: "DocFillImportMlsModal",
                                })
                              )
                            }
                          >
                            Close
                          </Button>
                        </div>
                      </Grid.Column>
                    </Grid.Row>
                  )}
                </Grid>
              </Form>
            )}
          </Formik>
        </div>
      </Segment>
    </ModalWrapper>
  );
}
